import { useDeleteCommentMutation, useDeleteFilesMutation } from 'api/collaborateApi';
import { Download, Eye, FileIcon, ImageIcon, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CommentFile } from 'types/api/tasks';
import { Button, Tooltip } from 'ui';
import { cn } from 'utils/cn';
import { isInIframe } from 'utils/proxyUtils';
import { setLightboxImageUrl, setLightboxOpen } from '../../../../store/uiSlice';

interface FileDisplayProps {
  downloadable?: boolean;
  file: CommentFile | File;
  isPendingFile?: boolean;
  removable?: boolean;
  className?: string;
  commentId?: string;
  taskId?: string;
  singleImage?: boolean; // New prop to indicate if this is a single image
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
}

export const FileDisplay = ({
  downloadable,
  file,
  isPendingFile = false,
  removable = false,
  className,
  commentId,
  taskId,
  singleImage = false, // Default to false
  setFiles
}: FileDisplayProps) => {
  const dispatch = useDispatch();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const isImage = file.type?.startsWith('image/');
  const [deleteFiles, { isLoading: isDeleting, isError }] =
    useDeleteFilesMutation();
  const [
    deleteComment,
    { isLoading: isDeletingComment, isError: isErrorComment },
  ] = useDeleteCommentMutation();

  useEffect(() => {
    if (isImage && file) {
      if ('url' in file && typeof file.url === 'string') {
        setPreviewUrl(file.url);
        return;
      } else {
        try {
          const url = URL.createObjectURL(file as unknown as Blob);
          setPreviewUrl(url);
          return () => URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Error creating object URL', error);
        }
      }
    }
  }, [file, isImage]);

  const handleOpenImage = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (previewUrl) {
      dispatch(setLightboxImageUrl(previewUrl));
      dispatch(setLightboxOpen(true));
    }
  };

  const handleDelete = async () => {
    if (!taskId) return;
    if (typeof file === 'string' && commentId) {
      await deleteComment({
        task_id: taskId,
        comment_id: commentId,
      });
    } else if (typeof file !== 'string' && 'id' in file && file.id) {
      await deleteFiles({
        task_id: taskId,
        ids: [file.id],
      });
    }
  };

  const handleDownload = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    let url;
    if ('url' in file && file.url) {
      url = file.url;
    } else if (previewUrl) {
      url = previewUrl;
    } else {
      return;
    }

    try {

      if (isInIframe()) {
        // If we're in an iframe, ask the parent to handle the download
        window.parent.postMessage({
          type: 'DOWNLOAD_FILE',
          url: url,
          filename: file.name || 'download'
        }, '*');

      } else {
        // If not in iframe, try to force download using fetch and blob
        fetch(url)
          .then(response => response.blob())
          .then(blob => {
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = file.name || 'download'; // Force download
            a.style.display = 'none';

            document.body.appendChild(a);
            a.click();

            // Clean up
            setTimeout(() => {
              document.body.removeChild(a);
              window.URL.revokeObjectURL(blobUrl);
            }, 100);
          })
          .catch(error => {
            console.error('Error fetching file for download:', error);

            // Fallback to direct download attempt
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name || 'download';
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          });
      }
    } catch (error) {
      console.error('Error initiating download:', error);

      // Last resort: just open in a new tab
      window.open(url, '_blank');
    }
  };

  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPendingFile) {
      setFiles((prevFiles: File[]) => prevFiles.filter((f) => f.name !== file.name));
    } else {
      await handleDelete();
    }
  };

  const parseFileSize = (size: number) => {
    if (isNaN(size)) return '0 Bytes';
    const units = ['Bytes', 'KB', 'MB'];
    let value = size;
    let index = 0;
    while (value > 1024 && index < units.length) {
      value /= 1024;
      index++;
    }

    return `${value.toFixed(2)} ${units[index]}`;
  };

  const getFileIcon = () => {
    return isImage ? <ImageIcon className="h-5 w-5" /> : <FileIcon size={26} />;
  };

  if (isImage && previewUrl) {
    return (
      <Tooltip content={file.name} portal={true}>
        <div
          className={cn(
            "group relative overflow-hidden rounded-lg cursor-pointer border border-gray-100",
            // If it's a single image, use max height instead of aspect-square
            singleImage ? "max-h-80" : "aspect-square",
            className
          )}
          onClick={handleOpenImage}
        >
          <img
            src={previewUrl}
            alt={file.name}
            className={cn(
              "w-full object-cover",
              // For single images, set height to auto to maintain aspect ratio
              singleImage ? "h-auto max-h-80" : "h-full object-top"
            )}
          />

          <div className="absolute inset-0 bg-black bg-opacity-20 transition-opacity hidden group-hover:flex group-hover:bg-opacity-50 items-center justify-center gap-2">
            <Button
              variant="ghost"
              className="hover:text-tertiary size-7 p-1 bg-background bg-opacity-80 hover:bg-opacity-100 rounded-full"
              onClick={handleOpenImage}
              tooltip="View image"
            >
              <Eye size={16} />
            </Button>

            {downloadable && (
              <Button
                variant="ghost"
                className="hover:text-tertiary size-7 p-1 bg-background bg-opacity-80 hover:bg-opacity-100 rounded-full"
                onClick={handleDownload}
                tooltip="Download image"
              >
                <Download size={16} />
              </Button>
            )}
          </div>

          {removable && (
            <Tooltip content={`Remove ${file.name}`} portal={true}>
              <button
                onClick={handleRemove}
                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-md bg-gray-100 text-destructive hover:bg-border"
              >
                <X className="h-3 w-3" />
              </button>
            </Tooltip>
          )}
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={file.name} portal={true}>
      <div
        className={cn(
          'group relative flex items-center gap-2 overflow-hidden rounded-lg hover:bg-gray-100 bg-gray-50 p-2 w-full',
          className
        )}
      >
        <div className="flex-shrink-0 text-ata-light-blue">
          {getFileIcon()}
        </div>
        <div className="flex min-w-0 flex-1 flex-col items-start justify-between max-w-[70%]">
          <span className="w-full truncate text-sm font-medium">
            {file.name}
          </span>
          <span className="w-full truncate text-sm leading-none text-gray-400">
            {parseFileSize(file.size)}
          </span>
        </div>

        <div className="flex gap-1 ml-auto">
          {downloadable && (
            <Button
              variant="ghost"
              className="hover:text-tertiary size-7 p-1 hover:bg-border"
              onClick={handleDownload}
              tooltip="Download file"
            >
              <Download size={16} />
            </Button>
          )}
        </div>

        {removable && (
          <Tooltip content={`Remove ${file.name}`}>
            <button
              onClick={handleRemove}
              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-md bg-gray-100 text-destructive hover:bg-border"
            >
              <X className="h-3 w-3" />
            </button>
          </Tooltip>
        )}
      </div>
    </Tooltip>
  );
};

export default FileDisplay;